import { useState } from 'react';
import {
  Loader2, Eye, Play, AlertTriangle, CheckCircle2,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { getErrorMessage } from '../../lib/errors';

export function StatementLandlordBackfillPanel() {
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null);

  const [applying, setApplying] = useState(false);
  const [applyResult, setApplyResult] = useState(null);

  const [error, setError] = useState(null);

  const runPreview = async () => {
    setPreviewing(true);
    setError(null);
    setPreview(null);
    setApplyResult(null);
    try {
      const { data } = await diagnosticsAPI.statementLandlordBackfillPreview();
      setPreview(data);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setPreviewing(false);
    }
  };

  const runApply = async () => {
    setApplying(true);
    setError(null);
    try {
      const { data } = await diagnosticsAPI.statementLandlordBackfillApply();
      setApplyResult(data);
      setPreview(null);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setApplying(false);
    }
  };

  const canApply = preview && preview.resolvable > 0 && !applying;

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">Statement Landlord Backfill</CardTitle>
          <Button
            variant="outline"
            size="sm"
            onClick={runPreview}
            disabled={previewing || applying}
          >
            {previewing ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Eye className="h-4 w-4 mr-2" />
            )}
            Preview
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          Finds management statements with null landlord_entity_id and resolves
          the landlord slug from the parsed name via the canonical entity registry.
        </p>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Idle */}
        {!preview && !applyResult && !error && !previewing && (
          <p className="text-sm text-muted-foreground">
            Press Preview to scan for statements needing landlord backfill.
          </p>
        )}

        {/* Error */}
        {error && (
          <div className="flex items-start gap-2 text-sm text-red-500">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {/* Preview: empty */}
        {preview && preview.total_null_landlord === 0 && (
          <div className="rounded-lg border p-4 text-center">
            <CheckCircle2 className="h-6 w-6 text-green-500 mx-auto mb-1" />
            <p className="text-sm text-muted-foreground">
              No statements need backfill.
            </p>
          </div>
        )}

        {/* Preview: results */}
        {preview && preview.total_null_landlord > 0 && (
          <div className="space-y-4">
            {/* Summary */}
            <div className="grid grid-cols-3 gap-3">
              <div className="rounded-lg border p-3">
                <p className="text-xs text-muted-foreground">Total null landlord</p>
                <p className="text-xl font-bold tabular-nums">{preview.total_null_landlord}</p>
              </div>
              <div className="rounded-lg border p-3">
                <p className="text-xs text-muted-foreground">Resolvable</p>
                <p className="text-xl font-bold tabular-nums text-green-600 dark:text-green-400">
                  {preview.resolvable}
                </p>
              </div>
              <div className="rounded-lg border p-3">
                <p className="text-xs text-muted-foreground">Unresolvable</p>
                <p className="text-xl font-bold tabular-nums text-yellow-600 dark:text-yellow-400">
                  {preview.unresolvable}
                </p>
              </div>
            </div>

            {/* Item table */}
            {preview.items?.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b bg-muted/30">
                      <th className="px-2 py-1 text-left font-medium">Statement ID</th>
                      <th className="px-2 py-1 text-left font-medium">Property</th>
                      <th className="px-2 py-1 text-left font-medium">Month</th>
                      <th className="px-2 py-1 text-left font-medium">Parsed Landlord</th>
                      <th className="px-2 py-1 text-left font-medium">Resolved Slug</th>
                      <th className="px-2 py-1 text-left font-medium">Entity</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.items.map((item) => (
                      <tr key={item.statement_id} className="border-t">
                        <td className="px-2 py-1 font-mono">{item.statement_id}</td>
                        <td className="px-2 py-1">{item.property_name_raw || '-'}</td>
                        <td className="px-2 py-1">{item.statement_month || '-'}</td>
                        <td className="px-2 py-1">{item.parsed_landlord_entity || '-'}</td>
                        <td className="px-2 py-1">
                          {item.resolved_slug ? (
                            <Badge className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                              {item.resolved_slug}
                            </Badge>
                          ) : (
                            <Badge className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400">
                              needs_entity_mapping
                            </Badge>
                          )}
                        </td>
                        <td className="px-2 py-1">{item.resolved_entity_label}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Apply button */}
            {canApply && (
              <div className="border-t pt-3">
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={runApply}
                  disabled={applying}
                >
                  {applying ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Play className="h-4 w-4 mr-2" />
                  )}
                  Apply Backfill ({preview.resolvable} statement{preview.resolvable !== 1 ? 's' : ''})
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Apply result */}
        {applyResult && (
          <div className="space-y-3">
            {applyResult.status === 'no_op' ? (
              <div className="rounded-lg border p-4 text-center">
                <CheckCircle2 className="h-6 w-6 text-green-500 mx-auto mb-1" />
                <p className="text-sm text-muted-foreground">{applyResult.message}</p>
              </div>
            ) : (
              <div className="rounded-lg border border-green-300 dark:border-green-800 p-3 bg-green-50 dark:bg-green-950/30 space-y-3">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
                  <span className="text-sm font-medium">Backfill applied</span>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-lg border p-3">
                    <div className="text-xs text-muted-foreground">Stamped</div>
                    <div className="text-lg font-bold tabular-nums text-green-600">
                      {applyResult.stamped}
                    </div>
                  </div>
                  <div className="rounded-lg border p-3">
                    <div className="text-xs text-muted-foreground">Flagged (needs mapping)</div>
                    <div className={`text-lg font-bold tabular-nums ${applyResult.flagged > 0 ? 'text-yellow-600' : ''}`}>
                      {applyResult.flagged}
                    </div>
                  </div>
                </div>

                {/* Details */}
                {applyResult.details?.length > 0 && (
                  <details className="space-y-2">
                    <summary className="text-sm font-semibold cursor-pointer">
                      Per-statement details ({applyResult.details.length})
                    </summary>
                    <div className="overflow-x-auto mt-2">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="border-b text-left text-muted-foreground">
                            <th className="py-1 pr-3 font-medium">Statement ID</th>
                            <th className="py-1 pr-3 font-medium">Action</th>
                            <th className="py-1 pr-3 font-medium">Parsed Name</th>
                            <th className="py-1 pr-3 font-medium">Slug</th>
                          </tr>
                        </thead>
                        <tbody>
                          {applyResult.details.map((d, i) => (
                            <tr key={i} className="border-b border-border/50">
                              <td className="py-1 pr-3 font-mono">{d.statement_id}</td>
                              <td className="py-1 pr-3">
                                <Badge
                                  variant={d.action === 'stamped' ? 'default' : 'outline'}
                                  className="text-[10px]"
                                >
                                  {d.action}
                                </Badge>
                              </td>
                              <td className="py-1 pr-3">{d.parsed_name || '-'}</td>
                              <td className="py-1 pr-3 font-mono">
                                {d.slug || (
                                  <span className="text-yellow-600">needs_entity_mapping</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </details>
                )}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
